import { ago, date, initials } from "@lumberset/takeoff-ui";
import {
  Avatar,
  Container,
  EmptyState,
  Input,
  SectionHeader,
  Skeleton,
  Table,
} from "@lumberset/ui";
import { MagnifyingGlass, UserGroup } from "@medusajs/icons";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useDeferredValue, useState } from "react";
import { Crumbs } from "../../../components/crumbs";
import { type PersonRow, peopleQuery } from "../../../lib/queries-people";
import { RoleBadge } from "../staff/-parts";
import { PersonStatus, seatText } from "./-parts";

export const Route = createFileRoute("/_app/people/")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(peopleQuery("", 0)),
  component: People,
});

/** How many of someone's workspaces a row names before saying how many more. */
const SHOWN_WORKSPACES = 2;

function lastActive(person: PersonRow) {
  if (person.status === "invited") return "Not signed in yet";
  return person.lastActiveAt ? `Active ${ago(person.lastActiveAt)}` : "No recent sign-in";
}

function People() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const q = useDeferredValue(text.trim());
  // Kept with the search it pages through: a new search starts at its first page.
  const [paging, setPaging] = useState({ q: "", page: 0 });
  const page = paging.q === q ? paging.page : 0;
  const { data, isPending } = useQuery(peopleQuery(q, page));

  return (
    <>
      <Crumbs trail={[{ label: "People" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="People"
          description="Everyone who can sign in to LumberSet, customers and Staff alike, newest first. Open someone to change their details or workspaces, or to sign in as them."
        />
        <div className="px-6 py-3 sm:max-w-sm">
          <Input
            type="search"
            placeholder="Name, email or workspace"
            aria-label="Search people by name, email or the name of a workspace they are in"
            prefix={<MagnifyingGlass />}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </div>

        {isPending || !data ? (
          <div className="flex flex-col gap-y-3 px-6 py-4">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ) : data.people.length === 0 ? (
          <EmptyState
            icon={<UserGroup />}
            title={q ? "Nobody matches" : "Nobody yet"}
            description={
              q
                ? "Try part of a name, an email, or a workspace's name."
                : "People appear here as soon as they sign up or are invited."
            }
          />
        ) : (
          <>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Person</Table.HeaderCell>
                  <Table.HeaderCell>Workspaces</Table.HeaderCell>
                  <Table.HeaderCell>Staff</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Joined</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data.people.map((person) => {
                  const more = person.workspaces.length - SHOWN_WORKSPACES;
                  return (
                    <Table.Row
                      key={person.id}
                      data-clickable="true"
                      onClick={() => navigate({ to: "/people/$id", params: { id: person.id } })}
                    >
                      <Table.Cell className="py-2.5">
                        <div className="flex w-[240px] items-center gap-x-3">
                          <Avatar fallback={initials(person.name)} shape="circle" size="small" />
                          <div className="min-w-0">
                            {/* A real link too, so the page opens from the keyboard or in a new tab. */}
                            <Link
                              to="/people/$id"
                              params={{ id: person.id }}
                              className="txt-compact-small-plus block truncate text-ui-fg-base outline-none hover:underline focus-visible:underline"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {person.name}
                            </Link>
                            <div
                              className="txt-compact-xsmall truncate text-ui-fg-muted"
                              title={person.email}
                            >
                              {person.email}
                            </div>
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="py-2.5">
                        {person.workspaces.length === 0 ? (
                          <span className="text-ui-fg-muted">None</span>
                        ) : (
                          <div className="flex max-w-[260px] flex-col">
                            {person.workspaces.slice(0, SHOWN_WORKSPACES).map((workspace) => (
                              <span
                                key={workspace.id}
                                className="truncate"
                                title={seatText(workspace)}
                              >
                                {seatText(workspace)}
                              </span>
                            ))}
                            {more > 0 ? (
                              <span className="txt-compact-xsmall text-ui-fg-muted">
                                and {more} more
                              </span>
                            ) : null}
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        {person.staffRole ? (
                          <RoleBadge role={person.staffRole} />
                        ) : (
                          <span className="text-ui-fg-muted">-</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap py-2.5">
                        <PersonStatus status={person.status} staff={person.staffRole !== null} />
                        <div className="txt-compact-xsmall mt-1 text-ui-fg-muted">
                          {lastActive(person)}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        {date(person.createdAt)}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
            <Table.Pagination
              page={data.page}
              pageSize={data.pageSize}
              count={data.total}
              onPageChange={(next) => setPaging({ q, page: next })}
            />
          </>
        )}
      </Container>
    </>
  );
}
